import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { Text, Title } from '@/shared/ui';

interface Props {
  outcome: 'approved' | 'rejected';
}

/**
 * Terminal confirmation screen shown after the tenant approves or rejects a vistory.
 *
 * @param {Props} props - The recorded approval outcome.
 * @returns {JSX.Element} Rendered confirmation panel.
 */
export function VistoryConfirmation({ outcome }: Props) {
  const { t } = useTranslation('features/vistories');
  const isApproved = outcome === 'approved';

  return (
    <div data-testid="vistory-confirmation" className="flex flex-col items-center gap-[var(--space-4)] text-center">
      <div className={cn(
        'flex h-16 w-16 items-center justify-center rounded-full',
        isApproved ? 'bg-[var(--status-success-subtle)]' : 'bg-[var(--status-error-subtle)]',
      )}>
        <Text span className="text-[length:var(--text-2xl)]">
          {isApproved ? '✓' : '✕'}
        </Text>
      </div>
      <Title order={2} size="lg" className="text-[var(--text-primary)]">
        {isApproved
          ? t('vistory_public.approved_title')
          : t('vistory_public.rejected_title')
        }
      </Title>
      <Text size="sm" className="text-[var(--text-secondary)]">
        {isApproved
          ? t('vistory_public.approved_message')
          : t('vistory_public.rejected_message')
        }
      </Text>
    </div>
  );
}
